import { useEffect, useState } from "react";

// Money input with es-AR formatting. Shows "." as thousand separator and
// "," as decimal separator while the user types. Accepts digits and an
// optional comma; everything else is filtered.
//
//   <MoneyInput value={form.monto} onChange={(v) => setForm({ ...form, monto: v })} />
//
// onChange receives a number (or "" when the field is empty). The component
// keeps its own display state so trailing commas like "1.234," survive while
// the user is still typing — but external value changes still sync the field.

// "1234,5" -> number 1234.5; "" or junk -> ""
const parseDisplay = (str) => {
  if (str === "" || str === null || str === undefined) return "";
  const cleaned = String(str).replace(/\./g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? "" : num;
};

// number 1234.5 -> "1.234,5"; integer 1234 -> "1.234" (no trailing ",00")
const formatFromNumber = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const n = Number(val);
  if (Number.isNaN(n)) return "";
  if (n === Math.trunc(n)) return n.toLocaleString("es-AR");
  return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Reformat user-typed string: keep digits + at most one comma + max 2 decimals
const cleanInput = (raw) => {
  let cleaned = String(raw).replace(/[^\d,]/g, "");
  const firstComma = cleaned.indexOf(",");
  if (firstComma === -1) {
    if (!cleaned) return "";
    return Number(cleaned).toLocaleString("es-AR");
  }
  const intPart = cleaned.slice(0, firstComma).replace(/,/g, "");
  const decPart = cleaned.slice(firstComma + 1).replace(/,/g, "").slice(0, 2);
  const intFormatted = intPart === "" ? "0" : Number(intPart).toLocaleString("es-AR");
  return decPart === "" ? `${intFormatted},` : `${intFormatted},${decPart}`;
};

function MoneyInput({ value, onChange, className = "", placeholder, required, ...rest }) {
  const [display, setDisplay] = useState(() => formatFromNumber(value));

  // Sync from external value changes (e.g., calculated values, form resets).
  // We only re-render display when the parsed number actually differs, so
  // intermediate states like "1.234," (trailing comma) survive.
  useEffect(() => {
    const current = parseDisplay(display);
    const incoming = value === "" || value === null || value === undefined ? "" : Number(value);
    if (current !== incoming) {
      setDisplay(formatFromNumber(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const next = cleanInput(e.target.value);
    setDisplay(next);
    onChange(parseDisplay(next));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      className={className}
      {...rest}
    />
  );
}

export default MoneyInput;
